import bigInt from 'big-integer';
export var TypeKind;
(function (TypeKind) {
    TypeKind["Primitive"] = "Primitive";
    TypeKind["List"] = "List";
    TypeKind["Enum"] = "Enum";
    TypeKind["Object"] = "Object";
    TypeKind["Union"] = "Union";
})(TypeKind || (TypeKind = {}));
export var SystemType;
(function (SystemType) {
    SystemType["Boolean"] = "Boolean";
    SystemType["Number"] = "Number";
    SystemType["String"] = "String";
    SystemType["MLString"] = "MLString";
    SystemType["BigNumber"] = "BigNumber";
})(SystemType || (SystemType = {}));
export const primitiveTypes = Object.values(SystemType).map(name => name.toLowerCase());
export function equableTypeGuard(subject) {
    return typeof subject['eq'] === 'function';
}
export class Type {
    constructor(name, kind) {
        this.kind = kind;
        this.name = name;
    }
    getTypeReference() {
        return this.name;
    }
}
export class PrimitiveType extends Type {
    constructor(name, valueType) {
        super(name, TypeKind.Primitive);
        this.valueType = valueType;
    }
}
export function primitiveTypeTypeGuard(subject) {
    return subject.kind === TypeKind.Primitive;
}
export class EnumType extends Type {
    constructor(name, valueType, values) {
        super(name, TypeKind.Enum);
        this.valueType = valueType;
        this.values = values;
    }
}
export class ListType extends Type {
    constructor(name, elementType = '', nullable = false) {
        super(name, TypeKind.List);
        this.nullable = nullable;
        this.elementType = elementType;
    }
}
export class Property {
    constructor(name, type, opts = {}) {
        this.opts = {
            needSource: false,
            constraints: [],
            availability: [],
            sourceOptions: null
        };
        this.name = name;
        this.type = type;
        Object.assign(this.opts, opts);
    }
}
export class ObjectType extends Type {
    constructor(name, properties, components = [], externals = [], compatibilityTables = []) {
        super(name, TypeKind.Object);
        this.components = components;
        this.externals = externals;
        this.compatibilityTables = compatibilityTables;
        this.propertyMap = {};
        this.properties = properties;
        for (let property of properties) {
            if (this.propertyMap[property.name]) {
                throw new Error('duplicate property:' + name);
            }
            this.propertyMap[property.name] = property;
        }
    }
    getProperty(name) {
        if (name === '_id') {
            return {
                name: '_id',
                opts: {},
                type: 'string'
            };
        }
        return this.propertyMap[name];
    }
}
export class BigNumber {
    constructor(value) {
        this.dotPosition = 0;
        this.dotChar = '.';
        if (!arguments.length) {
            this.value = bigInt(0);
            return;
        }
        value = value.toString();
        let dotPosition = 0;
        let dotFound = false;
        let index = 0;
        let numberValue = '';
        while (index < value.length) {
            const char = value[index];
            if (char === this.dotChar) {
                if (dotFound) {
                    throw new Error('Invalid number');
                }
                else {
                    dotFound = true;
                    dotPosition = value.length - index - 1;
                }
            }
            else {
                numberValue += char;
            }
            index++;
        }
        this.dotPosition = dotPosition;
        this.value = bigInt(numberValue);
        this.prettify();
    }
    static zero() {
        return new BigNumber(0);
    }
    static create(value, dotPosition) {
        const self = new BigNumber();
        self.value = value;
        self.dotPosition = dotPosition;
        self.prettify();
        return self;
    }
    plus(value) {
        const dotPosition = Math.max(this.dotPosition, value.dotPosition);
        let a = this.value;
        let b = value.value;
        if (this.dotPosition < dotPosition) {
            const multiplier = dotPosition - this.dotPosition;
            a = this.value.multiply(bigInt('10').pow(multiplier));
        }
        if (value.dotPosition < dotPosition) {
            const multiplier = dotPosition - value.dotPosition;
            b = value.value.multiply(bigInt('10').pow(multiplier));
        }
        return BigNumber.create(a.plus(b), dotPosition);
    }
    minus(value) {
        return this.plus(value.uminus());
    }
    uminus() {
        return BigNumber.create(this.value.multiply(-1), this.dotPosition);
    }
    toNumberParts() {
        const str = this.value.toString();
        if (this.dotPosition === 0) {
            return [str];
        }
        return [str.slice(0, -this.dotPosition), str.slice(-this.dotPosition)];
    }
    toString() {
        const numParts = this.toNumberParts();
        if (numParts.length === 1) {
            return numParts[0];
        }
        return numParts[0] + '.' + numParts[1];
    }
    eq(value) {
        const b = typeof value === 'number' ? new BigNumber(value) : value;
        return this.is('==', b);
    }
    mulBy(quantity) {
        if (quantity === 0) {
            return BigNumber.zero();
        }
        let result = this;
        const iterations = Math.abs(quantity);
        for (let i = 0; i < iterations - 1; i++) {
            result = result.plus(this);
        }
        return (quantity < 0) ? result.uminus() : result;
    }
    multiply(a) {
        const value = this.value.multiply(a.value);
        const dotPosition = this.dotPosition + a.dotPosition;
        return BigNumber.create(value, dotPosition);
    }
    prettify() {
        while (this.dotPosition > 0 && this.value.mod(10).eq(0)) {
            this.value = this.value.divide(10);
            this.dotPosition--;
        }
    }
    isNegative() {
        return !this.value.eq(0) && this.value.isNegative();
    }
    isPositive() {
        return !this.value.eq(0) && this.value.isPositive();
    }
    is(operator, value) {
        switch (operator) {
            case '<':
                return this.boolOperator(value, (a, b) => a.lesser(b));
            case '>':
                return this.boolOperator(value, (a, b) => a.greater(b));
            case '<=':
                return this.boolOperator(value, (a, b) => a.lesserOrEquals(b));
            case '>=':
                return this.boolOperator(value, (a, b) => a.greaterOrEquals(b));
            case '==':
                return this.boolOperator(value, (a, b) => a.equals(b));
            case '!=':
                return this.boolOperator(value, (a, b) => !a.eq(b));
        }
    }
    boolOperator(value, operator) {
        let c1 = this;
        let c2 = value;
        while (!(c1.dotPosition === 0 && c2.dotPosition === 0)) {
            c1 = c1.mulBy10();
            c2 = c2.mulBy10();
        }
        return operator(c1.value, c2.value);
    }
    mulBy10() {
        let value = this.value;
        let dotPosition = this.dotPosition;
        if (dotPosition === 0) {
            value = value.multiply(10);
        }
        else {
            dotPosition--;
        }
        return BigNumber.create(value, dotPosition);
    }
}
export class MLString {
    constructor(value) {
        this.value = value;
    }
    hashCode() {
        if (typeof this.value === 'string') {
            return this.value;
        }
        return Object.entries(this.value)
            .reduce((acc, [key, value]) => acc + `$__${key}__:${value};`, '');
    }
    eq(mlstring) {
        return this.hashCode() == mlstring.hashCode();
    }
}
MLString.empty = new MLString('');
export class UnionType extends Type {
    constructor(name, _types) {
        super(name, TypeKind.Union);
        this._types = _types;
    }
    get types() {
        return this._types;
    }
}
export function objectTypeTypeGuard(type) {
    return type.kind === TypeKind.Object;
}
//# sourceMappingURL=types.js.map